import padEnd from "lodash.padend";
import _truncate from "lodash.truncate";
import moment from "moment/moment";
import { EMPTY, Subject } from "rxjs";
import { ajax } from "rxjs/ajax";
import { catchError, mergeMap, map } from "rxjs/operators";
import { logLevelConsole, logLevelRemote } from "../../config";

const timestampFormat = "YYYY-MM-DD HH:mm:ss.SSS";

const httpOk = 200;

const levelLength = 5;
const loggerLength = 48;

const logLevels = {
  ALL: 0,
  TRACE: 1,
  DEBUG: 2,
  INFO: 3,
  WARN: 4,
  ERROR: 5,
  NONE: 10,
};

type LogLevel = keyof typeof logLevels;

let levelConsole = logLevelConsole as LogLevel;
let levelRemote = logLevelRemote as LogLevel;

const truncate = (str: string, length: number): string =>
  padEnd(
    _truncate(str, {
      length,
      omission: "",
    }),
    length,
  );

const compileMessage = (
  logger: string,
  level: string,
  message: string,
): string =>
  `${moment().format(timestampFormat)} | ${truncate(
    logger,
    loggerLength,
  )} | ${truncate(level, levelLength)} | ${message}`;

const logRemoteObservable = new Subject();

logRemoteObservable
  .pipe(
    map((logEntry) => ({
      url: "/commercial/log",
      timeout: 5000,
      responseType: "json",
      method: "POST",
      body: logEntry,
      headers: {
        "Content-Type": "application/json",
      },
    })),
    mergeMap((request) =>
      // @ts-expect-error Check
      ajax(request).pipe(
        catchError((error) => {
          if (error.status !== httpOk) {
            console.warn(`Could not send log message to the server: ${error}`);
          }
          return EMPTY;
        }),
      ),
    ),
  )
  .subscribe();

function logConsole(
  logger: string,
  level: string,
  msg: string,
  error?: Error,
): void {
  switch (level) {
    case "INFO":
      console.info(compileMessage(logger, level, msg));
      break;
    case "WARN":
      console.warn(compileMessage(logger, level, msg));
      break;
    case "ERROR":
      console.error(compileMessage(logger, level, msg), error);
      break;
    default:
      console.log(compileMessage(logger, level, msg));
  }
}

function logRemote(level: string, msg: string, error?: Error): void {
  let message = msg;
  if (error) {
    message = `${message} | ${JSON.stringify(error)}`;
  }

  logRemoteObservable.next({
    logLevel: level,
    message,
    clientTime: moment().toISOString(),
  });
}

function log(
  logger: string,
  level: LogLevel,
  msg: string,
  error?: Error,
): void {
  if (logLevels[level] >= logLevels[levelConsole]) {
    logConsole(logger, level, msg, error);
  }
  if (logLevels[level] >= logLevels[levelRemote]) {
    logRemote(level, msg, error);
  }
}

interface Logger {
  trace: (message: string) => void;
  debug: (message: string) => void;
  info: (message: string) => void;
  warn: (message: string, error?: Error) => void;
  error: (message: string, error?: Error) => void;
  console: (level: LogLevel) => void;
  remote: (level: LogLevel) => void;
}

const logger = (logger: string): Logger => ({
  trace: (message: string) => {
    log(logger, "TRACE", message);
  },

  debug: (message: string) => {
    log(logger, "DEBUG", message);
  },

  info: (message: string) => {
    log(logger, "INFO", message);
  },

  warn: (message: string, error?: Error) => {
    log(logger, "WARN", message, error);
  },

  error: (message: string, error?: Error) => {
    log(logger, "ERROR", message, error);
  },

  console: (level: LogLevel) => {
    levelConsole = level;
  },

  remote: (level: LogLevel) => {
    levelRemote = level;
  },
});

export default logger;
